import React from 'react';

import { useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import {
    Accordion,
    Image,
    Link,
    PageSection,
    Panel,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxVDPEstimateYourMonthlyPaymentClick, psxVDPFinanceClick, psxVDPGapInsuranceClick, psxVDPVehicleServiceContractsClick } from 'reaxl-analytics-handlers';

import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import ExplainerCard from './ExplainerCard';
import PsxHandleDelivery from './PsxHandleDelivery';
import PsxHowToPay from './PsxHowToPay';
import PsxMediaGalleryModalContainer from './PsxMediaGalleryModalContainer';
import PsxVerify from './PsxVerify';

const ExplainerCardsData = [
    {
        heading: 'Shop Verified Private Vehicles',
        subHeading: 'Chat with verified sellers, schedule test drives, and make offers online.',
    },
    {
        heading: 'Buy Online',
        subHeading: 'Pay online with our world-class lenders, your own bank or with cash.',
    },
    {
        heading: 'Grab the Keys and Go',
        subHeading: 'Pick up the car from the seller or have it delivered to your door.',
    },
];

function PsxExplainerContainer() {
    const showPsxExplainerContentPanel = useSelector(vdpActiveInteractionDuck.selectors.getShowPsxExplainerContent);
    const { id: listingId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const {
        psx_logo: [, {
            href: privateSellerExchangeLogoHref,
        }],
    } = useFeatures([
        'psx_logo',
    ]);

    const handleGapInsuranceLinkClick = (event) => {
        sendClick(psxVDPGapInsuranceClick, event, { listingId });
    };
    const handleFinancingLinkClick = (event) => {
        sendClick(psxVDPFinanceClick, event, { listingId });
    };
    const handleVehicleServiceContractLinkClick = (event) => {
        sendClick(psxVDPVehicleServiceContractsClick, event, { listingId });
    };
    const handleCalculatorJumpLinkClick = (event) => {
        sendClick(psxVDPEstimateYourMonthlyPaymentClick, event, { listingId });
    };

    const explainerCards = ExplainerCardsData.map(({
        heading,
        subHeading,
    }, index) => (
        <ExplainerCard
            heading={heading}
            subHeading={subHeading}
            key={heading}
            index={index}
        >
            {heading === 'Shop Verified Private Vehicles'
                && (
                    <PsxMediaGalleryModalContainer
                        videoId="psxHowItWorks"
                        uiContext="thumbnail"
                    />
                )}
            {heading === 'Buy Online'
                && (
                    <>
                        <Text weight="bold">
                            <Link
                                label="Financing,"
                                href="/marketplace/financing"
                                target="_blank"
                                onClick={handleFinancingLinkClick}
                                undecorated
                            />
                            {' '}
                            <Link
                                label="vehicle service contracts"
                                href="/marketplace/vehicle-service-contracts"
                                target="_blank"
                                onClick={handleVehicleServiceContractLinkClick}
                                undecorated
                            />
                            {' '}
                            and
                            {' '}
                            <Link
                                label="GAP insurance"
                                href="/marketplace/gap-insurance"
                                target="_blank"
                                onClick={handleGapInsuranceLinkClick}
                                undecorated
                            />
                            {' '}
                            available.
                        </Text>
                        <PsxMediaGalleryModalContainer
                            videoId="psxHowToPay"
                            uiContext="thumbnail"
                        />
                    </>
                )}
        </ExplainerCard>
    ));

    return (
        <PageSection
            id="psx-faq"
            alignment="left"
            className="col-lg-8"
            uiContext="section-3"
        >
            <Link
                href={privateSellerExchangeLogoHref}
                target="_blank"
            >
                <Image
                    src="/content/static/img/psx/kbb_srp_privatesellerexchange_logo.svg"
                    height={40}
                    width={228}
                    alt="Private Seller Exchange"
                    lazyload
                />
            </Link>
            <div className="margin-vertical-2">
                <Text weight="bold">
                    Test drive with sellers. Buy cars online. Clean title. No hassle.
                </Text>
                {' '}
                Free AutoCheck report, fraud protection, financing, vehicle service contracts and GAP insurance available.
            </div>
            <div
                className="display-flex flex-column flex-md-row gap-3 margin-bottom-3"
                data-cmp="psx-explainer-cards"
            >
                {explainerCards}
            </div>
            <Panel alignment="left">
                <Accordion
                    bsStyle="filters"
                    className="text-left"
                    defaultActiveKey={showPsxExplainerContentPanel}
                >
                    <PsxVerify />
                    <PsxHandleDelivery />
                    <PsxHowToPay onCalculatorJumpLinkClick={handleCalculatorJumpLinkClick} />
                </Accordion>
            </Panel>
        </PageSection>
    );
}

export default PsxExplainerContainer;
